import { format, parseISO } from 'date-fns';
import { toCurrency } from '@/utils';
import { RECOGNITION_REDEMPTION_METHOD_CHOICES } from '@/pages/recognition/constants';

// Kept out of ActivityTable.vue so Jest can test it: the repo's Jest can't mount <script setup>.

// A row's type is the combination of its kind (award vs unapproved program
// nomination) and the program type. recognition rows only ever exist for PROGRAM
// programs; award rows exist for all three program types.
const TYPE_LABELS = {
  'award:OPEN': 'Peer-to-peer recognition',
  'award:PROGRAM': 'Company award',
  'award:MILESTONE': 'Milestone award',
  'recognition:PROGRAM': 'Company award nomination',
};

// "Nomination withdrawn" keeps a nominator withdrawing apart from an admin withdrawing the money.
const STATUS_LABELS = {
  APPROVED: 'Approved',
  PENDING: 'Pending',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Nomination withdrawn',
};

// Single source of truth for redemption-method labels, shared with the other
// recognition tabs so the same grant type reads identically everywhere.
const GRANT_TYPE_LABELS = Object.fromEntries(
  RECOGNITION_REDEMPTION_METHOD_CHOICES.map(({ value, text }) => [value, text]),
);

export const formatDate = dateStr => {
  if (!dateStr) {
    return '-';
  }
  return format(parseISO(dateStr), 'd MMM yyyy');
};

export const formatType = item => TYPE_LABELS[`${item.kind}:${item.program_type}`] || item.program_type || '-';

export const formatStatus = item => STATUS_LABELS[item.nomination_status] || '-';

export const isUnapproved = item =>
  item.kind === 'recognition' && item.nomination_status in STATUS_LABELS && item.nomination_status !== 'APPROVED';

export const formatCompanyValues = values => (values && values.length ? values.join(', ') : '-');

const isEmptyAmount = value => value === null || value === undefined || value === 0;

export const formatAmount = (value, currency) => (isEmptyAmount(value) ? '-' : toCurrency(value, currency));

export const formatGrantType = grantType => GRANT_TYPE_LABELS[grantType] || '-';

const toExportNumber = value => (isEmptyAmount(value) ? '' : (value / 100).toFixed(2));

export const exportHeaders = [
  'Date',
  'Award',
  'Type',
  'Status',
  'From',
  'From employee no',
  'From division',
  'To',
  'To employee no',
  'To division',
  'Values',
  'Message',
  'Approved by',
  'Approver message',
  'Nominations',
  'Category',
  'Amount',
  'Redeemed',
  'Method',
  'Expiry',
  'Withdrawn',
  'Hidden',
];

export const exportRow = (item, fileFormat, { formatDivisionIds, currency }) => {
  const isDataExport = fileFormat === 'csv' || fileFormat === 'xlsx';
  return [
    formatDate(item.created_at),
    item.program_name || '-',
    formatType(item),
    formatStatus(item),
    item.recognised_by || 'Automatic',
    item.recognised_by_employee_code || '-',
    formatDivisionIds(item.recognised_by_division_ids),
    item.nominee_name || '-',
    item.employee_code || '-',
    formatDivisionIds(item.nominee_division_ids),
    formatCompanyValues(item.company_values),
    item.recipient_message || '-',
    item.approved_by || '-',
    item.approver_message || '-',
    item.nomination_count ?? '-',
    item.spend_category || '-',
    isDataExport ? toExportNumber(item.amount_minor) : formatAmount(item.amount_minor, currency),
    formatDate(item.redeemed_at),
    formatGrantType(item.redemption_grant_type),
    formatDate(item.expires_at),
    formatDate(item.withdrawn_at),
    item.is_hidden ? 'Yes' : '-',
  ];
};
